/**
 * **The one-shot claim that bounds the resume rung's reload — and the reason it is native.**
 *
 * When iOS kills the WKWebView's Networking process, `device-cache/store.ts` raises a
 * `StoreConnectionLostError` and the recovery is a page reload: the credentials are still on disk
 * and still correct, so a fresh document gets a fresh storage connection and the resident never
 * signs out. That recovery needs a bound — **an unbounded auto-reload on a deterministic failure is
 * an infinite boot loop on a store build, which is materially worse than the defect being fixed.**
 *
 * **The bound may not live in `localStorage`, and the design note that first prescribed it there
 * was wrong for this failure.** WebKit serves `localStorage` from **the same Networking process**
 * that just died, so the guard would be unreadable at exactly the moment it is consulted — a guard
 * that fails open, on the one platform it exists for. So the claim is held in native memory, across
 * a bridge that goes nowhere near WebKit storage.
 *
 * **It is held in memory on purpose, and that is the whole lifetime argument.** The claim must
 * survive a `location.reload()` — which replaces the document and leaves the process alone — and
 * must *not* survive a relaunch, because a relaunched app has a new Networking process and has
 * earned a fresh attempt. A plugin instance is registered once per process and outlives every
 * document in it, so an instance field has precisely that lifetime. Nothing is written to disk,
 * which also means there is no marker to leak, to clear on first launch, or to explain to D109's
 * artifact set.
 *
 * **Every failure declines, and the direction is not arbitrary.** No bridge, a rejecting call, an
 * unrecognised answer — all of them return `false`, which means *do not reload* and falls through
 * to the sign-out this rung exists to avoid. The opposite default would turn a bridge problem into
 * the boot loop above. **The stated cost is that the browser PWA gets no reload rung at all**: it
 * has no bridge, so `claimStoreReload()` always declines there, and an iOS Safari standalone
 * install — which has the same WebKit architecture and the same defect — still pays the OTP. That
 * is a real gap, taken deliberately over a `localStorage` guard that cannot be read when it counts.
 *
 * **Read the injected global, never an import.** `web/` must not depend on `@capacitor/core`: the
 * shell is P6's and the container is P18's, and `native/` consumes the built bundle rather than the
 * other way round — the same rule `nativeContainer.ts`, `auth/nativeHttp.ts` and `storeUpdate.ts`
 * state about this global. **`nativePromise`, not `Capacitor.Plugins.SeloGateRecoveryMarker`**:
 * those proxies are built by `registerPlugin` at *import* time, so in a bundle that refuses that
 * import the namespace is empty and the obvious-looking call is `undefined`.
 */
import { TIMED_OUT, deadline } from '../auth/timeout'
import { isNativeContainer } from './nativeContainer'

/** The plugin name. `SeloGateRecoveryMarkerPlugin` on both platforms answers to exactly this. */
const RECOVERY_MARKER_PLUGIN = 'SeloGateRecoveryMarker'

/**
 * 🔸 **Assumption, not a decision.** How long the claim waits for the bridge before declining.
 *
 * **It exists because a promise from a platform is a promise, not a guarantee** — R1-3's lesson,
 * which this repository has now paid for at the IndexedDB open and at the transaction. This one is
 * worse placed than either: it is awaited on the path that decides whether a resident keeps their
 * session, so a `nativePromise` that never settles would wedge the refresh instead of signing them
 * out — a hang where the pre-D309 behaviour was at least an answer.
 *
 * Short because the call does no I/O: the plugin flips a boolean under a lock and resolves. A false
 * timeout costs this occurrence its reload and nothing else — but only because the branch that
 * takes it **gives the claim back**: the native side sets its boolean whenever the call eventually
 * runs, so a timeout that walked away would spend the claim with no reload to show for it and
 * disable the rung for the whole app process.
 */
const CLAIM_TIMEOUT_MS = 1_000

interface CapacitorBridge {
  isNativePlatform?: () => boolean
  nativePromise?: (pluginName: string, methodName: string, options: unknown) => Promise<unknown>
}

/**
 * The two-term predicate `storeUpdate.ts` and `usesNativeBle()` use, for the same reason:
 * `isNativePlatform()` says this is a container, and `nativePromise` says the bridge is wired. A
 * browser fails the first; a container whose bridge has not injected yet fails the second.
 */
function bridge(): CapacitorBridge | undefined {
  const capacitor = (globalThis as typeof globalThis & { Capacitor?: CapacitorBridge }).Capacitor
  if (!isNativeContainer()) return undefined
  if (typeof capacitor?.nativePromise !== 'function') return undefined
  return capacitor
}

/**
 * Take the one reload this app process is allowed, and report whether this document got it.
 *
 * **The test and the set are one call on the native side**, not a `get` followed by a `set`. Two
 * documents cannot race here today — a reload replaces the one document — but a pair of calls is a
 * shape that only *looks* atomic, and the failure it would hide is the one thing this module
 * exists to prevent.
 */
export async function claimStoreReload(): Promise<boolean> {
  const capacitor = bridge()
  if (capacitor?.nativePromise === undefined) return false
  const budget = deadline(CLAIM_TIMEOUT_MS)
  try {
    const pending = capacitor.nativePromise(RECOVERY_MARKER_PLUGIN, 'claim', {})
    const answer = await budget.settle(pending)
    if (answer === TIMED_OUT) {
      // **A timeout does not cancel the call, so the claim is taken and nobody reloads.** The
      // native side sets its boolean unconditionally whenever it eventually runs, and the only
      // thing that hands it back is a completed IndexedDB operation — which on the device this
      // rung exists for never happens, because the connection is dead. Left alone, one slow
      // bridge answer on a memory-pressured resume would disable the reload for the whole app
      // process and leave the resident in exactly the pre-D309 lockout this rung was built to
      // end, which is not what "a false timeout costs the reload and nothing else" describes.
      //
      // **Released off the ORIGINAL promise rather than immediately**, so the release cannot
      // overtake the claim it is undoing — the bridge does not promise to run two calls in the
      // order they were made, and a `release` that lands first would leave the claim taken,
      // which is the state this branch exists to clear. A claim that never settles releases
      // nothing, which is today's behaviour and no worse.
      void pending.then(() => releaseStoreReloadClaim()).catch(() => undefined)
      return false
    }
    return (answer as { granted?: unknown } | null | undefined)?.granted === true
  } catch {
    // Declined, silently. A container built before this plugin existed rejects with *no such
    // plugin*, and the correct behaviour there is the behaviour before this rung: the sign-out.
    return false
  } finally {
    budget.cancel()
  }
}

/**
 * Hand the claim back, so a genuinely new occurrence hours later gets its own single reload.
 *
 * **The ordinary caller is one place and the position is the bound** — `device-cache/store.ts`'s
 * `noteDurableSuccess()`, on the first real IndexedDB operation that completes on a **durable**
 * handle. Releasing on anything weaker than a completed durable operation re-arms the reload on a
 * device where the reload did not work, which is the loop. Never rejects: the caller is on the
 * success path and a diagnostic must not break it.
 *
 * The one other caller is {@link claimStoreReload}'s timeout branch, undoing a claim that was
 * taken natively while nobody reloaded. That is not a weakening of the bound: it returns the
 * process to the state it was in before the call it is cancelling.
 */
export async function releaseStoreReloadClaim(): Promise<void> {
  const capacitor = bridge()
  if (capacitor?.nativePromise === undefined) return
  try {
    await capacitor.nativePromise(RECOVERY_MARKER_PLUGIN, 'release', {})
  } catch {
    /* The claim stays taken. One reload fewer, never one more. */
  }
}
